import {
  Badge,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverContent,
  PopoverBody,
  PopoverTrigger,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import {
  faChevronDown,
  faChevronRight,
  faPlus,
  faTrash,
  faCheck,
  faTimes,
  faEllipsis,
  faHashtag,
  faToggleOn,
  faRulerHorizontal,
  faSquare,
  faCube,
  faChevronCircleDown,
} from '@fortawesome/free-solid-svg-icons';

import React, { useState, useCallback } from 'react';
import { useMutation } from 'urql';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ShapeLabelIcon } from '../../assets/icons/shape-label-icon.svg';
import {
  ElementCategoryEnum,
  ElementCategoryType,
  DropdownOption,
  Project,
  TagColorScheme,
  UpdateProjectDocument,
} from '../../gql/graphql';
import SparkelIcon from '../common/icon/SparkelIcon';
import { useBreadcrumb } from '../common/BreadcrumbProvider';
import { EventName, useMixpanel } from '../../services/mixpanel';

type OrderLabelsTabProps = {
  project: Pick<Project, 'id' | 'settings'>;
};

export type ElementCategory = {
  name: string;
  type: ElementCategoryEnum;
  dropdownValues: DropdownOption[];
};

// eslint-disable-next-line complexity
export default function ElementCategoriesTab({
  project,
}: OrderLabelsTabProps): React.ReactElement {
  const { t } = useTranslation('company');
  const [, updateProject] = useMutation(UpdateProjectDocument);
  const { trackEvent } = useMixpanel();
  const { projectName } = useBreadcrumb();

  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategory, setNewCategory] = useState<
    Pick<ElementCategoryType, 'type' | 'dropdownValues'>
  >({
    type: ElementCategoryEnum.String,
    dropdownValues: [],
  });
  const [newDropdownValue, setNewDropdownValue] = useState('');
  const [newDropdownColor, setNewDropdownColor] = useState<TagColorScheme>(
    TagColorScheme.Blue
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const headerColor = useColorModeValue('gray.500', 'gray.500');
  const selectedBackgroundColor = useColorModeValue('blue.50', 'blue.800');
  const selectedTextColor = useColorModeValue('blue.700', 'blue.200');

  const handleAddNewCategory = useCallback(async () => {
    if (!newCategoryName.trim()) {
      return;
    }

    setIsSubmitting(true);

    trackEvent(EventName.SparkelAttributeCreated, {
      'Sparkel Attribute Name': newCategoryName,
      'Project Name': projectName,
    });

    const categoryToSave = {
      name: newCategoryName.trim(),
      type: newCategory.type,
      dropdownValues:
        newCategory.type === ElementCategoryEnum.Dropdown
          ? (newCategory.dropdownValues || []).map((option) => ({
              value: option.value,
              colorScheme: option.colorScheme,
            }))
          : null,
    };

    await updateProject({
      input: {
        id: project.id,
        patch: {
          settings: {
            ...project.settings,
            allowedElementsCategories: [
              ...(project.settings?.allowedElementsCategories ?? []),
              categoryToSave,
            ],
          },
        },
      },
    });

    setNewCategoryName('');
    setNewCategory({
      type: ElementCategoryEnum.String,
      dropdownValues: [],
    });
    setIsAddingNewCategory(false);
    setIsSubmitting(false);
  }, [
    newCategoryName,
    newCategory,
    project.id,
    project.settings,
    projectName,
    trackEvent,
    updateProject,
  ]);

  const handleCancelNewCategory = useCallback(() => {
    setNewCategoryName('');
    setNewCategory({
      type: ElementCategoryEnum.String,
      dropdownValues: [],
    });
    setIsAddingNewCategory(false);
  }, []);

  const handleDeleteElementCategory = useCallback(
    async (elementCategoryName: string) => {
      await updateProject({
        input: {
          id: project.id,
          patch: {
            settings: {
              ...project.settings,
              allowedElementsCategories: (
                project.settings?.allowedElementsCategories ?? []
              ).filter(
                (allowedElementCategory) =>
                  allowedElementCategory?.name !== elementCategoryName
              ),
            },
          },
        },
      });
    },
    [project.id, project.settings, updateProject]
  );

  const handleAddDropdownValue = useCallback(() => {
    if (!newDropdownValue.trim()) return;

    setNewCategory((prev) => ({
      ...prev,
      dropdownValues: [
        ...(prev.dropdownValues || []),
        {
          value: newDropdownValue.trim(),
          colorScheme: newDropdownColor,
        } as DropdownOption,
      ],
    }));
    setNewDropdownValue('');
  }, [newDropdownValue, newDropdownColor]);

  const handleRemoveDropdownValue = useCallback((valueToRemove: string) => {
    setNewCategory((prev) => ({
      ...prev,
      dropdownValues: (prev.dropdownValues || []).filter(
        (option) => option.value !== valueToRemove
      ),
    }));
  }, []);

  const getTypeDisplayName = (type: ElementCategoryEnum): string => {
    switch (type) {
      case ElementCategoryEnum.String:
        return 'Text';
      case ElementCategoryEnum.Number:
        return 'Number';
      case ElementCategoryEnum.Boolean:
        return 'Yes/No';
      case ElementCategoryEnum.Length:
        return 'Length';
      case ElementCategoryEnum.Area:
        return 'Area';
      case ElementCategoryEnum.Volume:
        return 'Volume';
      case ElementCategoryEnum.Dropdown:
        return 'Dropdown';
      default:
        return type;
    }
  };

  const getMenuItemStyles = (type: ElementCategoryEnum) => {
    const isSelected = newCategory.type === type;
    return {
      backgroundColor: isSelected ? selectedBackgroundColor : 'transparent',
      color: isSelected ? selectedTextColor : 'inherit',
      fontWeight: isSelected ? 'bold' : 'normal',
      icon: getAttributeTypeIcon(
        type,
        isSelected ? selectedTextColor : headerColor
      ),
    };
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Flex justifyContent={'space-between'} align="center">
        <Heading size={'md'}>{t('project-settings.attributes.title')}</Heading>
        <Button
          aria-label={t('project-settings.attributes.add-attribute-aria')}
          colorScheme="brand"
          size="xs"
          onClick={() => setIsAddingNewCategory(true)}
          leftIcon={<SparkelIcon icon={faPlus} color="inherit" />}
          isDisabled={isAddingNewCategory}
          variant="outline"
        >
          {t('project-settings.attributes.add-attribute')}
        </Button>
      </Flex>

      <Table variant="simple" size="sm" layout="fixed">
        <Thead>
          <Tr>
            <Th color={headerColor} fontWeight="normal" width="40%">
              {t('project-settings.attributes.name')}
            </Th>
            <Th
              color={headerColor}
              fontWeight="normal"
              width="calc(60% - 80px)"
            >
              {t('project-settings.attributes.type')}
            </Th>
            <Th width="80px"></Th>
          </Tr>
        </Thead>
        <Tbody>
          {project.settings?.allowedElementsCategories?.map((category) => (
            <Tr key={category.name} role="group">
              <Td>{category.name}</Td>
              <Td>
                <HStack>
                  <Badge
                    textTransform="none"
                    variant="subtle"
                    size="xs"
                    borderRadius="md"
                    px={2}
                    py={0.5}
                  >
                    <HStack>
                      {getAttributeTypeIcon(category.type, headerColor)}
                      <Text>{getTypeDisplayName(category.type)}</Text>
                    </HStack>
                  </Badge>
                  {category.type === ElementCategoryEnum.Dropdown &&
                    category.dropdownValues &&
                    category.dropdownValues.length > 0 && (
                      <Tooltip
                        label={category.dropdownValues
                          .map((option) => String(option.value || ''))
                          .filter(Boolean)
                          .join(', ')}
                        placement="top"
                        hasArrow
                      >
                        <Text fontSize="xs" color={headerColor}>
                          (
                          {t('project-settings.attributes.options-count', {
                            count: category.dropdownValues.length,
                          })}
                          )
                        </Text>
                      </Tooltip>
                    )}
                </HStack>
              </Td>
              <Td width="80px">
                <HStack spacing={1} justify="flex-end">
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Options"
                      size="xs"
                      variant="ghost"
                      icon={<SparkelIcon icon={faEllipsis} color="inherit" />}
                      colorScheme="gray"
                      opacity={0}
                      _groupHover={{
                        opacity: 1,
                        transition: 'opacity 0.2s ease-in-out',
                      }}
                    />
                    <MenuList>
                      <MenuItem
                        icon={<SparkelIcon icon={faTrash} color="red.400" />}
                        onClick={() =>
                          handleDeleteElementCategory(category.name)
                        }
                        color="red.400"
                      >
                        {t('project-settings.attributes.delete')}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </HStack>
              </Td>
            </Tr>
          ))}

          {isAddingNewCategory && (
            <Tr>
              <Td>
                <Input
                  autoFocus
                  size="sm"
                  w="100%"
                  placeholder={t('project-settings.attributes.attribute-name')}
                  value={newCategoryName}
                  variant="filled"
                  borderRadius="md"
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
              </Td>
              <Td>
                <HStack>
                  <Menu>
                    <MenuButton
                      as={Button}
                      size="sm"
                      borderRadius="md"
                      colorScheme="gray"
                      fontWeight="normal"
                      rightIcon={
                        <SparkelIcon
                          icon={faChevronDown}
                          size="xs"
                          color={headerColor}
                        />
                      }
                      leftIcon={getAttributeTypeIcon(
                        newCategory.type,
                        headerColor
                      )}
                      w="100%"
                      textAlign="left"
                    >
                      {getTypeDisplayName(newCategory.type)}
                    </MenuButton>
                    <MenuList padding={1}>
                      {Object.values(ElementCategoryEnum)
                        .filter((type) => type !== ElementCategoryEnum.Dropdown)
                        .map((type) => {
                          const styles = getMenuItemStyles(type);
                          return (
                            <MenuItem
                              key={type}
                              borderRadius="md"
                              icon={styles.icon}
                              onClick={() =>
                                setNewCategory((prev) => ({
                                  ...prev,
                                  type,
                                  dropdownValues: [],
                                }))
                              }
                              backgroundColor={styles.backgroundColor}
                              color={styles.color}
                              fontWeight={styles.fontWeight}
                            >
                              {getTypeDisplayName(type)}
                            </MenuItem>
                          );
                        })}
                      <Popover
                        placement="right-start"
                        closeOnBlur={false}
                        isOpen={
                          newCategory.type === ElementCategoryEnum.Dropdown
                        }
                      >
                        <PopoverTrigger>
                          <MenuItem
                            closeOnSelect={false}
                            borderRadius="md"
                            icon={
                              getMenuItemStyles(ElementCategoryEnum.Dropdown)
                                .icon
                            }
                            onClick={() =>
                              setNewCategory((prev) => ({
                                ...prev,
                                type: ElementCategoryEnum.Dropdown,
                                dropdownValues: prev.dropdownValues,
                              }))
                            }
                            backgroundColor={
                              getMenuItemStyles(ElementCategoryEnum.Dropdown)
                                .backgroundColor
                            }
                            color={
                              getMenuItemStyles(ElementCategoryEnum.Dropdown)
                                .color
                            }
                            fontWeight={
                              getMenuItemStyles(ElementCategoryEnum.Dropdown)
                                .fontWeight
                            }
                            _hover={{
                              backgroundColor:
                                newCategory.type ===
                                ElementCategoryEnum.Dropdown
                                  ? selectedBackgroundColor
                                  : 'var(--chakra-colors-gray-100)',
                            }}
                          >
                            <HStack justify="space-between" w="100%">
                              <Text>
                                {t('project-settings.attributes.type.dropdown')}
                              </Text>
                              <SparkelIcon
                                icon={faChevronRight}
                                size="xs"
                                color={
                                  newCategory.type ===
                                  ElementCategoryEnum.Dropdown
                                    ? selectedTextColor
                                    : headerColor
                                }
                              />
                            </HStack>
                          </MenuItem>
                        </PopoverTrigger>
                        <PopoverContent w="250px" borderRadius="lg">
                          <PopoverBody>
                            <VStack spacing={2} align="stretch">
                              {(newCategory.dropdownValues?.length || 0) > 0 ? (
                                <VStack spacing={1} align="stretch">
                                  {(newCategory.dropdownValues || []).map(
                                    (option, index) => (
                                      <HStack
                                        key={index}
                                        justify="space-between"
                                      >
                                        <Badge
                                          fontSize="sm"
                                          fontWeight="normal"
                                          maxW="80%"
                                          textTransform="none"
                                          colorScheme={
                                            (
                                              option.colorScheme as TagColorScheme
                                            ).toLowerCase() || 'gray'
                                          }
                                          variant="subtle"
                                          borderRadius="md"
                                          px={2}
                                          py={0.5}
                                        >
                                          {option.value || ''}
                                        </Badge>
                                        <IconButton
                                          size="xs"
                                          variant="ghost"
                                          aria-label={t(
                                            'project-settings.attributes.remove-value-aria'
                                          )}
                                          colorScheme="gray"
                                          icon={
                                            <SparkelIcon
                                              icon={faTimes}
                                              color="inherit"
                                            />
                                          }
                                          onClick={() =>
                                            handleRemoveDropdownValue(
                                              option.value || ''
                                            )
                                          }
                                        />
                                      </HStack>
                                    )
                                  )}
                                </VStack>
                              ) : (
                                <Text fontSize="xs" color={headerColor}>
                                  {t('project-settings.attributes.no-options')}
                                </Text>
                              )}
                              <HStack>
                                <Menu>
                                  <MenuButton
                                    as={Tag}
                                    size="xs"
                                    colorScheme={newDropdownColor.toLowerCase()}
                                    padding={2}
                                    cursor="pointer"
                                  />
                                  <MenuList>
                                    {Object.values(TagColorScheme).map(
                                      (color) => (
                                        <MenuItem
                                          key={color}
                                          onClick={() => {
                                            setNewDropdownColor(color);
                                          }}
                                        >
                                          <Tag
                                            size="xs"
                                            padding={1}
                                            colorScheme={color.toLowerCase()}
                                          >
                                            {color}
                                          </Tag>
                                        </MenuItem>
                                      )
                                    )}
                                  </MenuList>
                                </Menu>
                                <Input
                                  autoFocus
                                  size="xs"
                                  borderRadius="md"
                                  placeholder={t(
                                    'project-settings.attributes.add-option'
                                  )}
                                  value={newDropdownValue}
                                  variant="filled"
                                  onChange={(e) =>
                                    setNewDropdownValue(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleAddDropdownValue();
                                    }
                                  }}
                                />
                                <IconButton
                                  size="xs"
                                  aria-label={t(
                                    'project-settings.attributes.add-option-aria'
                                  )}
                                  icon={
                                    <SparkelIcon
                                      icon={faPlus}
                                      color="inherit"
                                    />
                                  }
                                  onClick={handleAddDropdownValue}
                                  isDisabled={!newDropdownValue.trim()}
                                />
                              </HStack>
                            </VStack>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    </MenuList>
                  </Menu>
                </HStack>
              </Td>
              <Td width="80px">
                <HStack spacing={1} justify="flex-end">
                  <IconButton
                    size="xs"
                    aria-label={t('project-settings.attributes.cancel-aria')}
                    icon={<SparkelIcon icon={faTimes} />}
                    onClick={handleCancelNewCategory}
                    colorScheme="gray"
                  />
                  <IconButton
                    size="xs"
                    aria-label={t(
                      'project-settings.attributes.save-attribute-aria'
                    )}
                    icon={<SparkelIcon icon={faCheck} color="inherit" />}
                    onClick={handleAddNewCategory}
                    isDisabled={
                      !newCategoryName.trim() ||
                      (newCategory.type === ElementCategoryEnum.Dropdown &&
                        (newCategory.dropdownValues?.length || 0) === 0)
                    }
                    isLoading={isSubmitting}
                    colorScheme="brand"
                  />
                </HStack>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>

      {(!project.settings?.allowedElementsCategories ||
        project.settings.allowedElementsCategories.length === 0) &&
        !isAddingNewCategory && (
          <Text color="gray.500" textAlign="center" py={4}>
            {t('project-settings.attributes.no-attributes')}
          </Text>
        )}
    </VStack>
  );
}

export const getAttributeTypeIcon = (
  type: ElementCategoryEnum,
  color?: string
) => {
  const iconColor = color ?? 'inherit';
  switch (type) {
    case ElementCategoryEnum.String:
      return <SparkelIcon fixedWidth as={ShapeLabelIcon} color={iconColor} />;
    case ElementCategoryEnum.Number:
      return <SparkelIcon fixedWidth icon={faHashtag} color={iconColor} />;
    case ElementCategoryEnum.Boolean:
      return <SparkelIcon fixedWidth icon={faToggleOn} color={iconColor} />;
    case ElementCategoryEnum.Dropdown:
      return (
        <SparkelIcon fixedWidth icon={faChevronCircleDown} color={iconColor} />
      );
    case ElementCategoryEnum.Length:
      return (
        <SparkelIcon fixedWidth icon={faRulerHorizontal} color={iconColor} />
      );
    case ElementCategoryEnum.Area:
      return <SparkelIcon fixedWidth icon={faSquare} color={iconColor} />;
    case ElementCategoryEnum.Volume:
      return <SparkelIcon fixedWidth icon={faCube} color={iconColor} />;
    default:
      return <SparkelIcon fixedWidth as={ShapeLabelIcon} color={iconColor} />;
  }
};
